import { Link } from 'react-router-dom';
import { ArrowRight, Users, Zap, Camera, Instagram } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';

const SERVICES_PATTERN = 'https://static.prod-images.emergentagent.com/jobs/a6bba23e-9f76-4699-a363-7b1495992596/images/eb21e65ea01742556997175d401df7e6aa4d3575962959edae50a4e9b2ce2689.png';

const services = [
  {
    icon: Users,
    title: 'Influencer Marketing',
    desc: 'We connect D2C brands with nano and micro influencers who drive real purchases — not just impressions. From sourcing to campaign execution, we handle it all. Every creator is vetted for engagement quality and audience relevance — not just follower count.',
    path: '/services/influencer-marketing',
    span: 'md:col-span-7',
  },
  {
    icon: Zap,
    title: 'AI UGC Ads',
    desc: 'Traditional shoots cost time and money that most D2C brands cannot afford to waste. We produce high-quality, scroll-stopping ad creatives using AI — content that looks like a professional brand shoot but is ready in days, not weeks.',
    path: '/services/ai-ugc-ads',
    span: 'md:col-span-5',
  },
  {
    icon: Camera,
    title: 'Real UGC Content',
    desc: 'Consumers trust real people more than polished brand content. Real UGC — made by actual creators using your product — builds credibility, drives trust, and consistently outperforms traditional ad creative on paid and organic channels.',
    path: '/services/real-ugc',
    span: 'md:col-span-5',
  },
  {
    icon: Instagram,
    title: 'Social Media Content',
    desc: 'We build complete Instagram content pipelines for D2C brands — and employer branding content for growing companies — using AI-generated visuals and UGC-style creatives. Consistent, on-brand, always ready to post, without a single shoot day.',
    path: '/services/social-media-content',
    span: 'md:col-span-7',
  },
];

export default function ServicesPage() {
  return (
    <>
      <SEOHead
        title="Marketing Services for D2C Brands | Thriveit Media"
        description="Thriveit Media offers influencer marketing, AI UGC ads, real UGC content, and social media content services for D2C and ecommerce brands. Based in Bengaluru."
        canonical="https://thriveitmedia.in/services"
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute right-0 top-0 w-1/3 h-full hidden lg:block opacity-20">
          <img src={SERVICES_PATTERN} alt="Abstract geometric grid pattern" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Our Services</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-4xl mb-8">
              Marketing Services Built for D2C Brands
            </h1>
            <p className="text-base md:text-lg font-body font-light text-[#888880] max-w-2xl leading-relaxed">
              We do not do everything. We do four things extremely well — and each one is built around a single goal: helping D2C brands and ecommerce companies produce content that converts and campaigns that drive real sales.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Service Grid */}
      <section data-testid="services-grid" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((s) => (
              <StaggerItem key={s.title} className={s.span}>
                <Link
                  to={s.path}
                  data-testid={`service-block-${s.path.split('/').pop()}`}
                  className="block h-full bg-[#111111] border border-white/5 p-8 md:p-12 group hover:border-[#FF5C35]/30 transition-all duration-500 hover:-translate-y-1"
                >
                  <s.icon className="w-10 h-10 text-[#FF5C35] mb-8" strokeWidth={1.5} />
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#F5F5F0] mb-4 group-hover:text-[#FF5C35] transition-colors">{s.title}</h2>
                  <p className="text-sm md:text-base font-body font-light text-[#888880] leading-relaxed mb-8">{s.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-body font-medium text-[#FF5C35] group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <CTABanner heading="Not Sure Which Service You Need?" subtext="Book a free 30-minute call and we will tell you exactly which service fits your brand right now — and what results you can realistically expect." />
    </>
  );
}
